<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useCompaniesStore } from 'dashboard/stores/companies';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const emit = defineEmits(['create']);

const { t } = useI18n();

const globalStore = useStore();
const companiesStore = useCompaniesStore();

const dialogRef = ref(null);
const isSubmitting = ref(false);

const formData = ref({
  title: '',
  value: '',
  currency: 'USD',
  stage: 'lead',
  probability: 20,
  expected_close_date: '',
  company_id: '',
  contact_id: '',
  user_id: '',
});

const rules = {
  title: { required },
};

const v$ = useVuelidate(rules, formData);

const isFormInvalid = computed(() => v$.value.$invalid);

const stageOptions = [
  { value: 'lead', label: t('DEALS.STAGES.LEAD') },
  { value: 'qualified', label: t('DEALS.STAGES.QUALIFIED') },
  { value: 'proposal', label: t('DEALS.STAGES.PROPOSAL') },
  { value: 'negotiation', label: t('DEALS.STAGES.NEGOTIATION') },
];

const currencyOptions = [
  { value: 'USD', label: '$ USD' },
  { value: 'EUR', label: '€ EUR' },
  { value: 'GBP', label: '£ GBP' },
  { value: 'JPY', label: '¥ JPY' },
  { value: 'CAD', label: 'C$ CAD' },
  { value: 'AUD', label: 'A$ AUD' },
  { value: 'NGN', label: '₦ NGN' },
];

// Company selector options
const companyOptions = computed(() => {
  return companiesStore.getCompaniesList.map(company => ({
    value: company.id,
    label: company.name || t('DEALS.UNTITLED'),
  }));
});

// Contact selector options from Vuex store
const contactOptions = computed(() => {
  const contacts = globalStore.getters['contacts/getContactsList'] || [];
  return contacts.map(contact => ({
    value: contact.id,
    label: contact.name || contact.email || t('DEALS.UNTITLED'),
  }));
});

// Agent selector options from Vuex store
const agentOptions = computed(() => {
  const agents = globalStore.getters['agents/getAgents'] || [];
  return agents.map(agent => ({
    value: agent.id,
    label: agent.name || agent.email || t('DEALS.UNTITLED'),
  }));
});

// Search handlers
const handleCompanySearch = query => {
  if (query.length >= 2) {
    companiesStore.search({ search: query });
  }
};

const handleContactSearch = query => {
  if (query.length >= 2) {
    globalStore.dispatch('contacts/search', { search: query });
  }
};

const open = () => {
  resetForm();
  // Preload data for selectors
  companiesStore.get({ page: 1 });
  globalStore.dispatch('contacts/get', { page: 1 });
  globalStore.dispatch('agents/get');
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const resetForm = () => {
  formData.value = {
    title: '',
    value: '',
    currency: 'USD',
    stage: 'lead',
    probability: 20,
    expected_close_date: '',
    company_id: '',
    contact_id: '',
    user_id: '',
  };
  if (v$.value) {
    v$.value.$reset();
  }
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  isSubmitting.value = true;
  try {
    const data = {
      title: formData.value.title,
      value: parseFloat(formData.value.value) || 0,
      currency: formData.value.currency,
      stage: formData.value.stage,
      probability: parseInt(formData.value.probability) || 0,
      expected_close_date: formData.value.expected_close_date || null,
    };
    
    // Only include relation IDs if they have valid values
    if (formData.value.company_id) {
      data.company_id = formData.value.company_id;
    }
    if (formData.value.contact_id) {
      data.contact_id = formData.value.contact_id;
    }
    if (formData.value.user_id) {
      data.user_id = formData.value.user_id;
    }
    
    emit('create', data);
    close();
  } catch (error) {
    useAlert(t('DEALS.CREATE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const onSuccess = () => {
  resetForm();
  close();
};

defineExpose({
  dialogRef,
  open,
  close,
  onSuccess,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('DEALS.CREATE_DIALOG.TITLE')"
    :description="$t('DEALS.CREATE_DIALOG.DESCRIPTION')"
  >
    <div class="flex flex-col gap-4 py-4">
      <Input
        v-model="formData.title"
        :label="$t('DEALS.FORM.TITLE_LABEL')"
        :placeholder="$t('DEALS.FORM.TITLE_PLACEHOLDER')"
        :error="v$.title.$error ? $t('DEALS.FORM.TITLE_REQUIRED') : ''"
        autofocus
      />
      
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div class="col-span-2">
          <Input
            v-model="formData.value"
            type="number"
            :label="$t('DEALS.FORM.VALUE_LABEL')"
            :placeholder="$t('DEALS.FORM.VALUE_PLACEHOLDER')"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('DEALS.FORM.CURRENCY_LABEL') }}
          </label>
          <ComboBox
            v-model="formData.currency"
            :options="currencyOptions"
          />
        </div>
        <Input
          v-model="formData.probability"
          type="number"
          :label="$t('DEALS.FORM.PROBABILITY_LABEL')"
          :placeholder="$t('DEALS.FORM.PROBABILITY_PLACEHOLDER')"
        />
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('DEALS.FORM.STAGE_LABEL') }}
          </label>
          <ComboBox
            v-model="formData.stage"
            :options="stageOptions"
            :placeholder="$t('DEALS.FORM.STAGE_LABEL')"
          />
        </div>
        <Input
          v-model="formData.expected_close_date"
          type="date"
          :label="$t('DEALS.FORM.EXPECTED_CLOSE_LABEL')"
        />
      </div>

      <!-- Relation selectors -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-n-strong">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('DEALS.FORM.COMPANY_LABEL') }}
          </label>
          <ComboBox
            v-model="formData.company_id"
            :options="companyOptions"
            :placeholder="$t('DEALS.FORM.COMPANY_PLACEHOLDER')"
            use-api-results
            @search="handleCompanySearch"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('DEALS.FORM.CONTACT_LABEL') }}
          </label>
          <ComboBox
            v-model="formData.contact_id"
            :options="contactOptions"
            :placeholder="$t('DEALS.FORM.CONTACT_PLACEHOLDER')"
            use-api-results
            @search="handleContactSearch"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('DEALS.FORM.ASSIGNEE_LABEL') }}
        </label>
        <ComboBox
          v-model="formData.user_id"
          :options="agentOptions"
          :placeholder="$t('DEALS.FORM.ASSIGNEE_PLACEHOLDER')"
        />
      </div>
    </div>
    
    <template #footer>
      <div class="flex justify-end gap-3">
        <Button
          :label="$t('DEALS.CANCEL')"
          variant="ghost"
          @click="close"
        />
        <Button
          :label="$t('DEALS.CREATE_DIALOG.SUBMIT')"
          :is-loading="isSubmitting"
          :disabled="isFormInvalid || isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>
